/**
 * The kinds of thing a person reviews before an agent may use them. One template per kind, in code (never in the
 * database): what its fields are, how one item reads on a line, which page draws it, and which protected action an
 * approved item unlocks. A template is granted to an agent in the agent's settings; the three verdicts — Approve,
 * Reject, Request revision — belong to the template and are the same everywhere, so an agent never passes options.
 */

/** A line of text, a block that keeps its line breaks, an address, a moment in time, or a thing with an id and the words a person reads. */
export type ReviewFieldType = "line" | "text" | "email" | "iso" | "ref";

export type ReviewFieldPart = {
  key: string; label: string; type: "line" | "email"; max: number;
  /** The shape it must have (a regular expression's source), and what to say when it has not. */
  pattern?: string; patternError?: string;
};

export type ReviewField = {
  key: string;
  label: string;
  type: ReviewFieldType;
  /** Characters this field may have. On a ref, each part carries its own. */
  max?: number;
  parts?: ReviewFieldPart[];
  /** Left out altogether when there is nothing to say. Every other field is required. */
  optional?: boolean;
  /**
   * How to write this field, in plain words, for the agent filling it in: `pandas help review` prints it under the
   * field and the prompt names it. Only where what a person needs is not obvious from the label.
   */
  hint?: string;
};

/** One filed item: a value per field of its template, a ref being an object of its parts. */
export type ReviewItem = Record<string, string | Record<string, string>>;

export type ReviewTemplate = {
  name: string;
  label: string;
  /** The one line the prompt gives an agent: when to file this rather than ask a question. */
  when: string;
  fields: ReviewField[];
  /** One item on a line, for a list, a card and the agent's own turn. */
  summary: (item: ReviewItem) => string;
  /** The page that draws this kind, inside the signed-in app, and its name as a person reads it in the list and at its top. */
  route: string;
  page: string;
  /** What it is for, in one line for the person deciding: the Request templates list. */
  purpose: string;
  /** The protected action an approved item unlocks (slice 12b), or none. */
  release: string | null;
  /**
   * Whose login the release goes out with: the agent's granted credential, the approved row the authority (D14, the
   * default) — or `person`, the person it was filed for, their own login and nothing else (a Slack message as them). Such
   * an item is filed only by a run working for a person, is addressed to that person always, and only they decide it.
   */
  sendAs?: "agent" | "person";
  /** Decided in the chat it was filed in, and nowhere else: no Review Pages card of its own. */
  inline?: boolean;
  /**
   * The scope type its items belong to (AIT-24): filed only from a run under a scope of that type, and each field named
   * here (a ref's part) must equal that scope's id in the tool named. None: filed from any run that holds the template.
   */
  scope?: { type: string; match: { field: string; part?: string; system: string }[] };
};

const ref = (item: ReviewItem, field: string, part: string): string => {
  const value = item[field];
  return value && typeof value === "object" ? (value[part] ?? "") : "";
};
const line = (item: ReviewItem, field: string): string => (typeof item[field] === "string" ? (item[field] as string) : "");

/** Where a Slack message goes: one conversation (a channel, a private channel, a DM), or the people of a DM or group DM. */
export const SLACK_CHANNEL = /^[CGD][A-Z0-9]{2,}$/;
export const SLACK_PEOPLE = /^[UW][A-Z0-9]{2,}(\s*,\s*[UW][A-Z0-9]{2,}){0,7}$/;
/** The Slack user ids of a DM or group DM, in the order they were written. */
export const slackPeople = (id: string): string[] => id.split(",").map((u) => u.trim()).filter(Boolean);

export const REVIEW_TEMPLATES: ReviewTemplate[] = [
  {
    name: "outreach-message",
    label: "Outreach message",
    when: "a message you want sent to a contact at an account, exactly as it would go out",
    fields: [
      { key: "account", label: "Account", type: "ref", parts: [{ key: "id", label: "id", type: "line", max: 64 }, { key: "name", label: "name", type: "line", max: 120 }] },
      {
        key: "contact",
        label: "Contact",
        type: "ref",
        parts: [
          { key: "id", label: "id", type: "line", max: 64 },
          { key: "name", label: "name", type: "line", max: 120 },
          { key: "email", label: "email", type: "email", max: 200 },
        ],
      },
      { key: "sender", label: "Sender", type: "ref", parts: [{ key: "email", label: "email", type: "email", max: 200 }] },
      { key: "subject", label: "Subject", type: "line", max: 200 },
      { key: "body", label: "Body", type: "text", max: 8000 },
      {
        key: "why", label: "Why now", type: "text", max: 1000,
        hint: "two short sentences in plain words, for the person deciding — no codes, no internal notes, no instructions to the reviewer",
      },
      // The door it goes out of, which the page says above the message. Email is the only one Outreach sends today.
      { key: "channel", label: "Channel", type: "line", max: 20, optional: true, hint: 'the only channel today is "email"; leave it out and it is email' },
      { key: "not_before", label: "Hold until", type: "iso", max: 40, optional: true },
      { key: "expires_at", label: "Drop after", type: "iso", max: 40, optional: true },
    ],
    summary: (item) => `${line(item, "subject")} → ${ref(item, "contact", "name")} (${ref(item, "account", "name")})`,
    route: "/review/outreach-message",
    page: "SalesOS Outreach Messages",
    purpose: "Messages an agent wants sent to a contact at an account, read exactly as they would go out.",
    release: "salesos-outreach",
    // Filed from the run of its account only, for that account (Dimi, 2026-10-27).
    scope: { type: "account", match: [{ field: "account", part: "id", system: "salesos" }] },
  },
  {
    name: "content-topic",
    label: "Content topic",
    when: "a topic you want to write about, with its brief, before any of it is written",
    fields: [
      { key: "topic", label: "Topic", type: "line", max: 200 },
      { key: "brief", label: "Brief", type: "text", max: 4000 },
      { key: "audience", label: "Audience", type: "line", max: 200 },
      { key: "format", label: "Format", type: "line", max: 80 },
      { key: "why", label: "Why now", type: "text", max: 1000 },
    ],
    summary: (item) => line(item, "topic"),
    route: "/review/content-topic",
    page: "Content topics",
    purpose: "Topics an agent wants to write about, each with its brief, decided before any of it is written.",
    release: null,
  },
  {
    name: "slack-message",
    label: "Slack message",
    when: "a message you want to send in Slack as the person you work for — to a channel, a thread or the people of a DM — exactly as it will appear",
    fields: [
      {
        key: "to",
        label: "To",
        type: "ref",
        parts: [
          {
            key: "id", label: "id", type: "line", max: 120,
            pattern: `${SLACK_CHANNEL.source}|${SLACK_PEOPLE.source}`,
            patternError: "is a channel id (C…, G… or D…) or the user ids of a DM or group DM (U…,U…), up to 8 people",
          },
          { key: "name", label: "name", type: "line", max: 300 },
        ],
        hint: 'a channel {"id":"C0123ABCD","name":"#sales"}, or the people of a DM or group DM by their Slack user ids {"id":"U01AB,U02CD","name":"Nikos Tzamos, Erti Karameta"} (users.lookupByEmail on slack-user finds an id); the name is what the person reads',
      },
      { key: "message", label: "Message", type: "text", max: 4000, hint: "exactly as it will appear in Slack, in Slack's own markdown (*bold*, _italic_, <https://…|link text>): nothing is added or changed" },
      {
        key: "thread",
        label: "In the thread",
        type: "ref",
        optional: true,
        parts: [
          { key: "ts", label: "ts", type: "line", max: 32, pattern: "^\\d{9,11}\\.\\d{6}$", patternError: "is the parent message's ts (1729512345.123456)" },
          { key: "first_line", label: "first line", type: "line", max: 200 },
        ],
        hint: "only for a reply: the parent message's ts and its first line, so the person sees what it answers",
      },
      { key: "why", label: "Why", type: "line", max: 300, hint: "one line, for the person deciding" },
    ],
    summary: (item) => `${ref(item, "to", "name")}: ${line(item, "message").split("\n")[0]!.slice(0, 120)}`,
    route: "/review/slack-message",
    page: "Slack messages",
    purpose: "Messages an agent wants to send in Slack as you, read exactly as they will appear.",
    release: "slack-user",
    sendAs: "person",
    inline: true,
  },
];

export const reviewTemplate = (name: string): ReviewTemplate | null => REVIEW_TEMPLATES.find((t) => t.name === name) ?? null;

/** How many items one `pandas review` call may file into one batch. */
export const MAX_REVIEW_ITEMS = 20;

/** The names, for a picker and for the prompt. */
export const REVIEW_TEMPLATE_NAMES = REVIEW_TEMPLATES.map((t) => t.name);

/**
 * The words `pandas propose` and `pandas requests` already mean something by: a template named one of them would
 * be shadowed, or would shadow a kind (D14). A template is never renamed without a migration recipe either: its name
 * lives in `approvals.template`, the grants, the stored connector policies and the page route.
 */
export const RESERVED_TEMPLATE_NAMES = ["goal", "schedule", "agent", "sub-agent", "note", "user-note", "skill", "file-share", "instructions", "description", "question", "review", "workflow", "task", "list", "withdraw", "cancel", "release"];
for (const name of REVIEW_TEMPLATE_NAMES) {
  if (RESERVED_TEMPLATE_NAMES.includes(name)) throw new Error(`the request template '${name}' takes a word pandas propose already means something by`);
}

/** A field's value as a person reads it, whatever its type. */
export function reviewFieldText(field: ReviewField, value: string | Record<string, string> | undefined): string {
  if (value === undefined || value === null) return "";
  if (typeof value === "string") return value;
  return (field.parts ?? []).map((p) => value[p.key] ?? "").filter(Boolean).join(" · ");
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function cleanValue(label: string, type: "line" | "text" | "email" | "iso", max: number, raw: unknown): { ok: true; value: string } | { ok: false; error: string } {
  if (typeof raw !== "string") return { ok: false, error: `${label} must be text` };
  const value = type === "text" ? raw.replace(/\r\n/g, "\n").trim() : raw.replace(/\s+/g, " ").trim();
  if (!value) return { ok: false, error: `${label} is missing` };
  if (value.length > max) return { ok: false, error: `${label} is ${value.length} characters; at most ${max}` };
  if (type === "email" && !EMAIL.test(value)) return { ok: false, error: `${label} is not an email address` };
  // A moment is kept in one spelling, so what a person approved and what the platform later sends are the same bytes.
  if (type === "iso") {
    const at = Date.parse(value);
    if (Number.isNaN(at)) return { ok: false, error: `${label} is not a date and time (use 2026-11-12T09:00:00Z)` };
    return { ok: true, value: new Date(at).toISOString() };
  }
  return { ok: true, value };
}

/**
 * One item against its template: every field present, nothing else, nothing over its limit. The reason names the
 * field, so an agent fixes the whole call in one go. Trimming and line endings are settled here, which is also
 * what 12b compares word for word when it releases an approved item.
 */
export function cleanReviewItem(template: ReviewTemplate, raw: unknown): { ok: true; item: ReviewItem } | { ok: false; error: string } {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return { ok: false, error: `an item of ${template.name} is an object with the fields ${template.fields.map((f) => f.key).join(", ")}` };
  const given = raw as Record<string, unknown>;
  const known = new Set(template.fields.map((f) => f.key));
  const extra = Object.keys(given).filter((k) => !known.has(k));
  if (extra.length) return { ok: false, error: `${template.name} has no field '${extra[0]}'; its fields are ${template.fields.map((f) => f.key).join(", ")}` };
  const item: ReviewItem = {};
  for (const field of template.fields) {
    if (field.type === "ref") {
      const value = given[field.key];
      if (field.optional && (value === undefined || value === null)) continue;
      if (value === undefined || value === null) return { ok: false, error: `${field.label} is missing` };
      if (typeof value !== "object" || Array.isArray(value)) {
        return { ok: false, error: `${field.label} is an object with ${(field.parts ?? []).map((p) => p.key).join(" and ")}` };
      }
      const parts = field.parts ?? [];
      const partKeys = new Set(parts.map((p) => p.key));
      const spare = Object.keys(value as Record<string, unknown>).filter((k) => !partKeys.has(k));
      if (spare.length) return { ok: false, error: `${field.label} has no part '${spare[0]}'; it takes ${parts.map((p) => p.key).join(" and ")}` };
      const out: Record<string, string> = {};
      for (const part of parts) {
        const cleaned = cleanValue(`${field.label} ${part.label}`, part.type, part.max, (value as Record<string, unknown>)[part.key]);
        if (!cleaned.ok) return cleaned;
        if (part.pattern && !new RegExp(`^(?:${part.pattern})$`).test(cleaned.value)) return { ok: false, error: `${field.label} ${part.label} ${part.patternError ?? "is not in the expected form"}` };
        out[part.key] = cleaned.value;
      }
      item[field.key] = out;
      continue;
    }
    const raw = given[field.key];
    // An optional field left out, or left empty, is simply not part of the item: nothing to show and nothing to send.
    if (field.optional && (raw === undefined || raw === null || (typeof raw === "string" && !raw.trim()))) continue;
    const cleaned = cleanValue(field.label, field.type, field.max ?? 1000, raw);
    if (!cleaned.ok) return cleaned;
    item[field.key] = cleaned.value;
  }
  return { ok: true, item };
}
